<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import metaLeadgenConfigsAPI from 'dashboard/api/metaLeadgenConfigs';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const WHATSAPP_PROVIDERS = [
  'evolution',
  'waha',
  'uazapi',
  'baileys',
  'notificame',
  'whatsapp_cloud',
];

const DEFAULT_FIELD_MAPPING = [
  { source: 'full_name', target: 'name' },
  { source: 'email', target: 'email' },
  { source: 'phone_number', target: 'phone' },
];

const TARGET_FIELDS = ['name', 'email', 'phone'];

const isLoading = ref(true);
const isSaving = ref(false);
const configs = ref([]);

const blankForm = () => ({
  id: null,
  channel_facebook_page_id: '',
  funnel_id: '',
  stage_name: '',
  whatsapp_inbox_id: '',
  first_message_template: '',
  enabled: true,
  field_mapping: DEFAULT_FIELD_MAPPING.map(row => ({ ...row })),
});

const form = ref(blankForm());
const showForm = ref(false);

const inboxes = computed(() => getters['inboxes/getInboxes'].value);

const facebookInboxes = computed(() =>
  inboxes.value.filter(inbox => inbox.channel_type === INBOX_TYPES.FB)
);

const whatsappInboxes = computed(() =>
  inboxes.value.filter(inbox => {
    if (inbox.channel_type === INBOX_TYPES.WHATSAPP) return true;
    if (inbox.channel_type === INBOX_TYPES.API) {
      const attrs = inbox.additional_attributes || {};
      if (attrs.channel_identifier === 'whatsapp') return true;
      const provider = (inbox.provider || attrs.provider || '').toLowerCase();
      if (WHATSAPP_PROVIDERS.includes(provider)) return true;
      if (inbox.name?.toLowerCase().includes('whatsapp')) return true;
    }
    return false;
  })
);

const funnels = computed(() => getters['funnels/getFunnels'].value);

const formStages = computed(() => {
  if (!form.value.funnel_id) return [];
  return getters['funnels/getFunnelStages'].value(form.value.funnel_id);
});

const facebookPageLabel = channelId => {
  const inbox = facebookInboxes.value.find(
    item => item.channel_id === channelId
  );
  return inbox ? inbox.name : `#${channelId}`;
};

const funnelLabel = funnelId => {
  const funnel = funnels.value.find(item => item.id === funnelId);
  return funnel ? funnel.name : `#${funnelId}`;
};

const loadConfigs = async () => {
  try {
    const { data } = await metaLeadgenConfigsAPI.get();
    configs.value = data.payload || [];
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEADGEN.API.LOAD_ERROR'));
  }
};

onMounted(async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      store.dispatch('inboxes/get'),
      store.dispatch('funnels/get'),
      loadConfigs(),
    ]);
  } finally {
    isLoading.value = false;
  }
});

const onSelectFunnel = () => {
  // Reset stage when funnel changes if no longer valid
  const validStage = formStages.value.some(
    stage => stage.name === form.value.stage_name
  );
  if (!validStage) {
    form.value.stage_name = formStages.value[0]?.name || '';
  }
};

const openCreate = () => {
  form.value = blankForm();
  showForm.value = true;
};

const openEdit = config => {
  const mappingEntries = Object.entries(config.field_mapping || {}).map(
    ([source, target]) => ({ source, target })
  );
  form.value = {
    id: config.id,
    channel_facebook_page_id: config.channel_facebook_page_id || '',
    funnel_id: config.funnel_id || '',
    stage_name: config.stage_name || '',
    whatsapp_inbox_id: config.whatsapp_inbox_id || '',
    first_message_template: config.first_message_template || '',
    enabled: config.enabled,
    field_mapping: mappingEntries.length
      ? mappingEntries
      : DEFAULT_FIELD_MAPPING.map(row => ({ ...row })),
  };
  showForm.value = true;
};

const cancelForm = () => {
  showForm.value = false;
  form.value = blankForm();
};

const addMappingRow = () => {
  form.value.field_mapping.push({ source: '', target: 'name' });
};

const removeMappingRow = index => {
  form.value.field_mapping.splice(index, 1);
};

const buildPayload = () => {
  const fieldMapping = {};
  form.value.field_mapping.forEach(row => {
    if (row.source && row.target) {
      fieldMapping[row.source.trim()] = row.target;
    }
  });
  return {
    channel_facebook_page_id: form.value.channel_facebook_page_id,
    funnel_id: form.value.funnel_id,
    stage_name: form.value.stage_name,
    whatsapp_inbox_id: form.value.whatsapp_inbox_id || null,
    first_message_template: form.value.first_message_template,
    enabled: form.value.enabled,
    field_mapping: fieldMapping,
  };
};

const isFormValid = computed(
  () =>
    form.value.channel_facebook_page_id &&
    form.value.funnel_id &&
    form.value.stage_name
);

const saveConfig = async () => {
  if (!isFormValid.value) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEADGEN.API.VALIDATION_ERROR'));
    return;
  }
  isSaving.value = true;
  try {
    const payload = buildPayload();
    if (form.value.id) {
      await metaLeadgenConfigsAPI.update(form.value.id, payload);
      useAlert(t('INTEGRATION_SETTINGS.META_LEADGEN.API.UPDATE_SUCCESS'));
    } else {
      await metaLeadgenConfigsAPI.create(payload);
      useAlert(t('INTEGRATION_SETTINGS.META_LEADGEN.API.CREATE_SUCCESS'));
    }
    await loadConfigs();
    cancelForm();
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEADGEN.API.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const deleteConfig = async config => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('INTEGRATION_SETTINGS.META_LEADGEN.DELETE_CONFIRM'))) {
    return;
  }
  try {
    await metaLeadgenConfigsAPI.delete(config.id);
    useAlert(t('INTEGRATION_SETTINGS.META_LEADGEN.API.DELETE_SUCCESS'));
    await loadConfigs();
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.META_LEADGEN.API.DELETE_ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('INTEGRATION_SETTINGS.META_LEADGEN.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('INTEGRATION_SETTINGS.META_LEADGEN.TITLE')"
        :description="$t('INTEGRATION_SETTINGS.META_LEADGEN.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-6">
        <!-- Intro / requirements -->
        <div
          class="bg-n-alpha-2 border border-n-weak rounded-md p-4 text-sm text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.INTRO') }}
        </div>

        <!-- Existing configs list -->
        <div v-if="!showForm" class="flex flex-col gap-4">
          <div class="flex items-center justify-between">
            <h3 class="text-base font-semibold text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.LIST.TITLE') }}
            </h3>
            <Button
              icon="i-lucide-plus"
              :label="$t('INTEGRATION_SETTINGS.META_LEADGEN.LIST.ADD')"
              @click="openCreate"
            />
          </div>

          <div
            v-if="!configs.length"
            class="text-sm text-n-slate-11 border border-dashed border-n-weak rounded-md p-6 text-center"
          >
            {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.LIST.EMPTY') }}
          </div>

          <div
            v-for="config in configs"
            :key="config.id"
            class="flex items-center justify-between border border-n-weak rounded-md p-4 bg-n-alpha-3"
          >
            <div class="flex flex-col gap-1">
              <div class="flex items-center gap-2">
                <span class="font-medium text-n-slate-12">
                  {{ facebookPageLabel(config.channel_facebook_page_id) }}
                </span>
                <span
                  class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="
                    config.enabled
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                      : 'bg-n-alpha-2 text-n-slate-11'
                  "
                >
                  {{
                    config.enabled
                      ? $t('INTEGRATION_SETTINGS.META_LEADGEN.LIST.ENABLED')
                      : $t('INTEGRATION_SETTINGS.META_LEADGEN.LIST.DISABLED')
                  }}
                </span>
              </div>
              <p class="text-xs text-n-slate-11">
                {{ funnelLabel(config.funnel_id) }}
                <!-- eslint-disable-next-line vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
                <span aria-hidden="true">&middot;</span>
                {{ config.stage_name }}
              </p>
            </div>
            <div class="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                icon="i-lucide-pencil"
                :label="$t('INTEGRATION_SETTINGS.META_LEADGEN.LIST.EDIT')"
                @click="openEdit(config)"
              />
              <Button
                variant="outline"
                color="ruby"
                size="sm"
                icon="i-lucide-trash-2"
                :label="$t('INTEGRATION_SETTINGS.META_LEADGEN.LIST.DELETE')"
                @click="deleteConfig(config)"
              />
            </div>
          </div>
        </div>

        <!-- Form -->
        <form
          v-else
          class="flex flex-col gap-4 border border-n-weak rounded-md p-6 bg-n-alpha-3"
          @submit.prevent="saveConfig"
        >
          <h3 class="text-base font-semibold text-n-slate-12">
            {{
              form.id
                ? $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.EDIT_TITLE')
                : $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.CREATE_TITLE')
            }}
          </h3>

          <!-- Facebook Page -->
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.FB_PAGE') }}
            </span>
            <select v-model="form.channel_facebook_page_id" class="form-select">
              <option value="" disabled>
                {{
                  $t(
                    'INTEGRATION_SETTINGS.META_LEADGEN.FORM.FB_PAGE_PLACEHOLDER'
                  )
                }}
              </option>
              <option
                v-for="inbox in facebookInboxes"
                :key="inbox.id"
                :value="inbox.channel_id"
              >
                {{ inbox.name }}
              </option>
            </select>
            <span
              v-if="!facebookInboxes.length"
              class="text-xs text-yellow-700 dark:text-yellow-500"
            >
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.NO_FB_PAGES') }}
            </span>
          </label>

          <!-- Funnel -->
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.FUNNEL') }}
            </span>
            <select
              v-model="form.funnel_id"
              class="form-select"
              @change="onSelectFunnel"
            >
              <option value="" disabled>
                {{
                  $t(
                    'INTEGRATION_SETTINGS.META_LEADGEN.FORM.FUNNEL_PLACEHOLDER'
                  )
                }}
              </option>
              <option
                v-for="funnel in funnels"
                :key="funnel.id"
                :value="funnel.id"
              >
                {{ funnel.name }}
              </option>
            </select>
          </label>

          <!-- Stage -->
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.STAGE') }}
            </span>
            <select
              v-model="form.stage_name"
              class="form-select"
              :disabled="!form.funnel_id"
            >
              <option value="" disabled>
                {{
                  $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.STAGE_PLACEHOLDER')
                }}
              </option>
              <option
                v-for="stage in formStages"
                :key="stage.name"
                :value="stage.name"
              >
                {{ stage.name }}
              </option>
            </select>
          </label>

          <!-- WhatsApp inbox -->
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.WHATSAPP_INBOX') }}
            </span>
            <select v-model="form.whatsapp_inbox_id" class="form-select">
              <option value="">
                {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.WHATSAPP_NONE') }}
              </option>
              <option
                v-for="inbox in whatsappInboxes"
                :key="inbox.id"
                :value="inbox.id"
              >
                {{ inbox.name }}
              </option>
            </select>
            <span class="text-xs text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.WHATSAPP_HINT') }}
            </span>
          </label>

          <!-- First message template -->
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.FIRST_MESSAGE') }}
            </span>
            <textarea
              v-model="form.first_message_template"
              rows="3"
              class="form-textarea"
              :placeholder="
                $t(
                  'INTEGRATION_SETTINGS.META_LEADGEN.FORM.FIRST_MESSAGE_PLACEHOLDER'
                )
              "
            />
            <span class="text-xs text-n-slate-11">
              {{
                $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.FIRST_MESSAGE_HINT')
              }}
            </span>
          </label>

          <!-- Field mapping -->
          <div class="flex flex-col gap-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.MAPPING') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.MAPPING_HINT') }}
            </span>
            <div
              v-for="(row, index) in form.field_mapping"
              :key="index"
              class="flex items-center gap-2"
            >
              <input
                v-model="row.source"
                type="text"
                class="form-input flex-1"
                :placeholder="
                  $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.MAPPING_SOURCE')
                "
              />
              <!-- eslint-disable-next-line vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
              <span class="text-n-slate-11" aria-hidden="true">&rarr;</span>
              <select v-model="row.target" class="form-select flex-1">
                <option
                  v-for="field in TARGET_FIELDS"
                  :key="field"
                  :value="field"
                >
                  {{ field }}
                </option>
              </select>
              <Button
                type="button"
                variant="ghost"
                color="ruby"
                size="sm"
                icon="i-lucide-trash-2"
                @click="removeMappingRow(index)"
              />
            </div>
            <Button
              type="button"
              variant="faded"
              size="xs"
              icon="i-lucide-plus"
              :label="$t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.MAPPING_ADD')"
              @click="addMappingRow"
            />
          </div>

          <!-- Enabled toggle -->
          <label class="flex items-center gap-2">
            <input v-model="form.enabled" type="checkbox" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.ENABLED') }}
            </span>
          </label>

          <div class="flex items-center gap-2 pt-2">
            <Button
              type="submit"
              icon="i-lucide-save"
              :label="$t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.SAVE')"
              :is-loading="isSaving"
              :disabled="!isFormValid"
            />
            <Button
              type="button"
              variant="outline"
              icon="i-lucide-arrow-left"
              :label="$t('INTEGRATION_SETTINGS.META_LEADGEN.FORM.CANCEL')"
              @click="cancelForm"
            />
          </div>
        </form>
      </div>
    </template>
  </SettingsLayout>
</template>
